import { Component, ElementRef, forwardRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';

import { PasswordInfo } from '@core/model/password-info';
import { ErrorStateMatcher } from '@angular/material/core';
import { ValidatorService } from '../validator.service';

export class PasswordErrorStateMatcher extends ErrorStateMatcher {
  constructor(private group: FormGroup) {
    super();
  }

  isErrorState(control: FormControl | null): boolean {
    return control?.dirty && (control.invalid || this.group.hasError('mismatch'));
  }
}

@Component({
  selector: 'app-rapi-password',
  templateUrl: './rapi-password.component.html',
  styleUrls: ['./rapi-password.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RapiPasswordComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RapiPasswordComponent),
      multi: true,
    },
  ],
})
export class RapiPasswordComponent implements OnInit, ControlValueAccessor, OnDestroy {
  form: FormGroup;
  passwordErrorStateMatcher: PasswordErrorStateMatcher;
  subscriptions: Subscription[] = [];
  @ViewChild('passwordInput', { static: false }) passwordInput: ElementRef;

  constructor(private formBuilder: FormBuilder, private validatorService: ValidatorService) {
    this.form = this.formBuilder.group(
      {
        password: ['', [Validators.required, Validators.pattern('^([a-zA-Z0-9]){6,}$')]],
        passwordConfirmation: ['', [Validators.required, Validators.pattern('^([a-zA-Z0-9]){6,}$')]],
      },
      {
        updateOn: 'change',
        validators: this.validatorService.matchingInputsValidator('password', 'passwordConfirmation'),
      }
    );

    this.passwordErrorStateMatcher = new PasswordErrorStateMatcher(this.form);

    this.subscriptions.push(
      this.form.valueChanges.subscribe((value) => {
        this.onChange(value);
        this.onTouched();
      })
    );
  }

  ngOnInit() {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  get value(): PasswordInfo {
    return this.form.value;
  }

  set value(value: PasswordInfo) {
    this.form.setValue(value);
    this.onChange(value);
    this.onTouched();
  }

  get passwordControl() {
    return this.form.controls.password;
  }

  get passwordConfirmationControl() {
    return this.form.controls.passwordConfirmation;
  }

  writeValue(value: any): void {
    if (value) {
      this.value = value;
    }

    if (value === null) {
      this.form.reset();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
      this.passwordInput.nativeElement.focus();
    }
  }

  validate(_: FormControl) {
    return this.form.valid ? null : { passwordInfo: { valid: false } };
  }
}
